import { AssignGroupManagerDialog } from "@components/table/members/remove-xg-seat/assign-group-manager-dialog";
import { MemberName, RemoveXgSeatList } from "@components/table/members/remove-xg-seat/remove-xg-seat-list";
import { getEditorGroups, getGroupChangeBlockReason, isOnlyGroupManager } from "@components/table/members/remove-xg-seat/remove-xg-seat-utils";
import { useMemberGroupsWithDetails } from "@components/table/members/remove-xg-seat/use-member-groups-with-details";
import { usePendingIds } from "@components/table/members/remove-xg-seat/use-pending-ids";
import { useRefreshMemberGroup } from "@components/table/members/remove-xg-seat/use-refresh-member-group";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { removeGroup as removeMemberGroup } from "@store/company-member-groups/company-member-groups-slice";
import { fetchGroupDetails, removeMemberFromGroupWithoutErrorToast, updateMemberRoleInGroupWithoutErrorToast } from "@store/groups/groups-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { formatUserRoleType } from "@utils/data-display";
import { useState } from "react";

/** What the member gives up in a group: the group as a whole, or only their editor role in it */

/** The title of the error toast when a change failed for another reason than the group manager */
const GROUP_CHANGE_ERROR_TITLES = {
  remove: "Cannot remove the member from the group",
  downgrade: "Cannot make the member a group viewer"
};

/** A change that waits for the group to get another group manager first */

/**
 * Step of taking a Sphere XG seat away from a member that lists the groups in which the member
 * is a group manager or a project manager, and lets the user remove the member from them or make
 * them a group viewer instead.
 *
 * The backend does not let the only group manager of a group step down. When that is why a change
 * failed, the user is asked for a new group manager first, and the change is made again after.
 */
export function RemoveXgSeatGroupsStep({
  member,
  onBusyChange,
  onResolvedChange
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    groups,
    status
  } = useMemberGroupsWithDetails(member.identity, "editorGroups");
  const {
    pendingIds,
    runWhilePending
  } = usePendingIds(onBusyChange);
  const refreshGroup = useRefreshMemberGroup(member.identity);
  const [pendingManagerChange, setPendingManagerChange] = useState(null);
  const editorGroups = getEditorGroups(groups);
  const changeableGroups = editorGroups.filter(group => !getGroupChangeBlockReason(group));
  const name = <MemberName member={member} />;
  return <>
      <RemoveXgSeatList status={status} description={<>
            {name} is a group manager or a project manager in these groups.
            Remove {name} from them, or make {name} a group viewer.
          </>} emptyMessage={<>{name} is not a group manager or a project manager in any group.</>} failedMessage="The groups of the member could not be loaded." items={editorGroups.map(toItem)} pendingIds={pendingIds} onResolvedChange={onResolvedChange} bulkActions={[{
      label: "Make group viewer in all",
      onClick: () => void changeGroups(changeableGroups, "downgrade")
    }, {
      label: "Remove from all groups",
      variant: "destructiveGhost",
      onClick: () => void changeGroups(changeableGroups, "remove")
    }]} />

      {pendingManagerChange && <AssignGroupManagerDialog member={member} group={pendingManagerChange.group} groupMembers={pendingManagerChange.groupMembers} onClose={() => setPendingManagerChange(null)} onAssigned={() => {
      setPendingManagerChange(null);
      void changeGroups([pendingManagerChange.group, ...pendingManagerChange.remainingGroups], pendingManagerChange.change);
    }} />}
    </>;

  /** Returns how a group is listed */
  function toItem(group_0) {
    return {
      id: group_0.id,
      name: group_0.name,
      details: group_0.role ? `Group role: ${formatUserRoleType(group_0.role)}` : "",
      blockReason: getGroupChangeBlockReason(group_0),
      actions: [{
        label: "Make group viewer",
        onClick: () => void changeGroups([group_0], "downgrade")
      }, {
        label: "Remove",
        variant: "destructiveGhost",
        onClick: () => void changeGroups([group_0], "remove")
      }]
    };
  }

  /**
   * Changes the role of the member in the groups one after the other, so that a group asking for a
   * new group manager does not get lost among the others. The changes pause there, since the user
   * has to choose the new group manager first, and go on with the remaining groups after.
   */
  async function changeGroups(groupsToChange, change) {
    await runWhilePending(groupsToChange.map(({
      id
    }) => id), async () => {
      for (const [index, group_1] of groupsToChange.entries()) {
        const groupMembers = await changeGroup(group_1, change);
        if (groupMembers) {
          setPendingManagerChange({
            group: group_1,
            groupMembers,
            change,
            remainingGroups: groupsToChange.slice(index + 1)
          });
          return;
        }
      }
    });
  }

  /**
   * Changes the role of the member in a group, which drops the group from the list unless the
   * member still is an editor there through a team. A group in which that failed stays listed.
   *
   * When it failed because the member is the only group manager, the user is asked for a new one
   * instead of being shown the error, which says nothing they could act on.
   *
   * @returns the members of the group when it needs a new group manager first, undefined otherwise
   */
  async function changeGroup(group_2, change_0) {
    try {
      await requestGroupChange(group_2, change_0);
    } catch (error) {
      const groupMembers_0 = await getMembersIfOnlyManager(group_2);
      if (!groupMembers_0) {
        handleErrorWithToast({
          id: `removeXgSeatGroupChange-${Date.now().toString()}`,
          title: GROUP_CHANGE_ERROR_TITLES[change_0],
          error
        });
      }
      return groupMembers_0;
    }
    await refreshGroup(group_2.id, () => dispatch(removeMemberGroup(group_2.id)));
    return undefined;
  }

  /** Sends the request that changes the role of the member in the group, rejecting on failure */
  async function requestGroupChange(group_3, change_1) {
    if (change_1 === "remove") {
      await dispatch(removeMemberFromGroupWithoutErrorToast({
        companyId,
        groupId: group_3.id,
        member,
        // The group is only dropped from the list of the dialog
        onGroupRemoved: () => undefined
      })).unwrap();
      return;
    }
    await dispatch(updateMemberRoleInGroupWithoutErrorToast({
      companyId,
      groupId: group_3.id,
      userIdentity: member.identity,
      role: "GROUP_VIEWER"
    })).unwrap();
  }

  /**
   * Returns the members of the group when the member is its only group manager, which is why
   * the backend refuses to change their role. Undefined otherwise, or when that is unknown.
   */
  async function getMembersIfOnlyManager(group_4) {
    try {
      const {
        members
      } = await dispatch(fetchGroupDetails({
        companyId,
        groupId: group_4.id
      })).unwrap();
      return isOnlyGroupManager(members, member.identity) ? members : undefined;
    } catch {
      return undefined;
    }
  }
}